<template>
  <div class="reviews-section">
    <div class="reviews-header">
      <h2>{{ t.reviews?.title }}</h2>
      <div class="reviews-summary" v-if="count > 0">
        <div class="stars-big">
          <span v-for="i in 5" :key="i" class="star-big" :class="i <= Math.round(average) ? 'filled' : ''">★</span>
        </div>
        <span class="avg-number">{{ average }}</span>
        <span class="avg-count">({{ count }})</span>
      </div>
    </div>

    <!-- Lista opinii -->
    <div v-if="reviews.length" class="reviews-list">
      <div v-for="r in reviews" :key="r.id" class="review-card">
        <div class="review-top">
          <div class="review-stars">
            <span v-for="i in 5" :key="i" class="star" :class="i <= r.rating ? 'filled' : ''">★</span>
          </div>
          <span v-if="r.is_verified" class="verified-badge">✓ {{ t.reviews?.verified }}</span>
        </div>
        <div class="review-title" v-if="r.title">{{ r.title }}</div>
        <div class="review-body">{{ r.body }}</div>
        <div class="review-meta">{{ r.author_name }} · {{ formatDate(r.created_at) }}</div>
      </div>
    </div>
    <div v-else class="no-reviews">{{ t.reviews?.no_reviews }}</div>

    <!-- Formularz -->
    <div class="review-form-wrap">
      <button class="btn-write" @click="showForm = !showForm">
        {{ showForm ? '✕ Zamknij' : '✏️ ' + t.reviews?.write_review }}
      </button>
      <p class="only-verified-note">🔒 {{ t.reviews?.only_verified }}</p>

      <form v-if="showForm" class="review-form" @submit.prevent="submitReview">
        <div class="form-row-2">
          <label>
            {{ t.reviews?.your_order }} *
            <input v-model="form.order_number" type="text" required placeholder="np. 1042" />
          </label>
          <label>
            {{ t.reviews?.your_email }} *
            <input v-model="form.email" type="email" required placeholder="jan@example.com" />
          </label>
        </div>
        <label>
          {{ t.reviews?.your_name }} *
          <input v-model="form.author_name" type="text" required placeholder="Jan K." />
        </label>
        <label>
          {{ t.reviews?.your_rating }} *
          <div class="star-picker">
            <span
              v-for="i in 5" :key="i"
              class="star-pick"
              :class="{ filled: i <= (hoverRating || form.rating) }"
              @mouseenter="hoverRating = i"
              @mouseleave="hoverRating = 0"
              @click="form.rating = i"
            >★</span>
          </div>
        </label>
        <label>
          {{ t.reviews?.your_title }}
          <input v-model="form.title" type="text" placeholder="Świetny produkt!" />
        </label>
        <label>
          {{ t.reviews?.your_body }}
          <textarea v-model="form.body" rows="4" placeholder="Podziel się swoją opinią..."></textarea>
        </label>
        <div v-if="formError" class="form-error">⚠️ {{ formError }}</div>
        <div v-if="formSuccess" class="form-success">✅ {{ t.reviews?.success }}</div>
        <button type="submit" class="btn-submit" :disabled="submitting || !form.rating">
          {{ submitting ? 'Wysyłanie...' : t.reviews?.submit }}
        </button>
      </form>
    </div>
  </div>
</template>

<script>
import { translations } from '@/locales/lang'
export default {
  name: 'AppProductReviews',
  props: { productId: { type: [Number,String], required: true } },
  data() {
    return {
      reviews: [], count: 0, average: 0,
      showForm: false, submitting: false,
      formError: null, formSuccess: false,
      hoverRating: 0,
      form: { order_number:'', email:'', author_name:'', rating:0, title:'', body:'' }
    }
  },
  computed: { t() { return translations.value } },
  async mounted() { await this.loadReviews() },
  methods: {
    async loadReviews() {
      try {
        const r = await fetch(`/api/v1/reviews/${this.productId}/`)
        const d = await r.json()
        this.reviews = d.reviews || []
        this.count   = d.count || 0
        this.average = d.average || 0
      } catch(e) { console.error(e) }
    },
    formatDate(d) {
      return d ? new Date(d).toLocaleDateString('pl-PL', {day:'2-digit',month:'long',year:'numeric'}) : ''
    },
    async submitReview() {
      if (!this.form.rating) return
      this.submitting = true; this.formError = null; this.formSuccess = false
      try {
        const csrfToken = document.cookie.split('; ').find(r=>r.startsWith('csrftoken='))?.split('=')[1]||''
        const r = await fetch('/api/v1/reviews/', {
          method: 'POST',
          headers: { 'Content-Type':'application/json', 'X-CSRFToken': csrfToken },
          body: JSON.stringify({ ...this.form, product_id: this.productId })
        })
        const d = await r.json()
        if (r.ok) {
          this.formSuccess = true
          this.form = { order_number:'', email:'', author_name:'', rating:0, title:'', body:'' }
          setTimeout(() => { this.showForm = false; this.formSuccess = false }, 3000)
        } else {
          const errs = Object.values(d).flat()
          this.formError = errs[0] || 'Błąd wysyłania opinii'
        }
      } catch(e) { this.formError = 'Błąd połączenia' }
      finally { this.submitting = false }
    }
  }
}
</script>

<style scoped>
.reviews-section { max-width: 860px; margin: 48px auto; padding: 0 16px 48px; }
.reviews-header { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:24px; }
.reviews-header h2 { font-size:1.4rem; font-weight:800; margin:0; }
.reviews-summary { display:flex; align-items:center; gap:8px; }
.stars-big .star-big { font-size:1.4rem; color:#ddd; }
.stars-big .star-big.filled { color:#f59e0b; }
.avg-number { font-size:1.4rem; font-weight:800; }
.avg-count { color:#888; font-size:.9rem; }
.reviews-list { display:flex; flex-direction:column; gap:16px; margin-bottom:32px; }
.review-card { background:#fff; border:1px solid #f0f0f0; border-radius:12px; padding:18px 20px; }
.review-top { display:flex; align-items:center; gap:10px; margin-bottom:8px; }
.star, .star-big { font-size:1.1rem; color:#ddd; }
.star.filled, .star-big.filled { color:#f59e0b; }
.verified-badge { background:#dcfce7; color:#166534; font-size:.74rem; font-weight:700; padding:2px 10px; border-radius:9999px; }
.review-title { font-weight:700; font-size:.95rem; margin-bottom:6px; }
.review-body { font-size:.9rem; color:#444; line-height:1.6; }
.review-meta { font-size:.78rem; color:#aaa; margin-top:10px; }
.no-reviews { color:#aaa; font-size:.95rem; padding:16px 0; }
.review-form-wrap { margin-top:24px; }
.btn-write { padding:10px 24px; background:#111; color:#fff; border:none; border-radius:9999px; font-weight:700; font-size:.9rem; cursor:pointer; transition:background .15s; }
.btn-write:hover { background:#333; }
.only-verified-note { font-size:.8rem; color:#888; margin:8px 0 0; }
.review-form { background:#f9f9f9; border-radius:12px; padding:20px; margin-top:16px; display:flex; flex-direction:column; gap:12px; }
.form-row-2 { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
.review-form label { display:flex; flex-direction:column; gap:4px; font-size:.85rem; font-weight:600; color:#333; }
.review-form input, .review-form textarea {
  padding:9px 12px; border:1px solid #e0e0e0; border-radius:8px;
  font-size:.875rem; outline:none; background:#fff;
  transition:border .15s;
}
.review-form input:focus, .review-form textarea:focus { border-color:#111; }
.star-picker { display:flex; gap:4px; margin-top:4px; }
.star-pick { font-size:1.8rem; color:#ddd; cursor:pointer; transition:color .1s; }
.star-pick.filled { color:#f59e0b; }
.form-error { background:#fef2f2; color:#dc2626; border-radius:8px; padding:10px 14px; font-size:.85rem; }
.form-success { background:#dcfce7; color:#166534; border-radius:8px; padding:10px 14px; font-size:.85rem; }
.btn-submit { padding:12px; background:#111; color:#fff; border:none; border-radius:10px; font-weight:700; cursor:pointer; transition:background .15s; }
.btn-submit:hover:not(:disabled) { background:#333; }
.btn-submit:disabled { opacity:.5; cursor:not-allowed; }
@media(max-width:600px) { .form-row-2 { grid-template-columns:1fr; } }
</style>
